import React, { useState, useEffect, useMemo } from 'react';

function StatsOverview({ stats, displayMode = 'dollars', unitSize = 25, bets = [], getUnitSizeForDate, advancedUnitSettings = false }) {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 50);
        return () => clearTimeout(timer);
    }, []);

    const unitStats = useMemo(() => {
        if (displayMode !== 'units' || !advancedUnitSettings || !getUnitSizeForDate) {
            return null;
        }

        let totalPLUnits = 0;
        let totalStakedUnits = 0;
        let totalReturnedUnits = 0;

        bets.forEach(bet => {
            const betUnitSize = getUnitSizeForDate(bet.date);
            totalPLUnits += bet.pl / betUnitSize;
            if (!bet.bonus_bet) {
                totalStakedUnits += bet.stake / betUnitSize;
            }
            if (bet.result !== 'pending') {
                totalReturnedUnits += ((bet.bonus_bet ? 0 : bet.stake) + bet.pl) / betUnitSize;
            }
        });

        return {
            totalPL: totalPLUnits,
            totalStaked: totalStakedUnits,
            totalReturned: totalReturnedUnits
        };
    }, [displayMode, advancedUnitSettings, bets, getUnitSizeForDate]);

    const formatValue = (value, showPrefix = true) => {
        if (displayMode === 'units') {
            const units = advancedUnitSettings && unitStats ? value : value / unitSize;
            const prefix = showPrefix ? (units >= 0 ? '+' : '') : '';
            return `${prefix}${units.toFixed(1)}U`;
        }
        const prefix = showPrefix ? (value >= 0 ? '+' : '') : '';
        return `${prefix}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatStaked = (value) => {
        if (displayMode === 'units') {
            const units = advancedUnitSettings && unitStats ? value : value / unitSize;
            return `${units.toFixed(1)}U`;
        }
        return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    };

    const displayTotalPL = displayMode === 'units' && advancedUnitSettings && unitStats ? unitStats.totalPL : stats.totalPL;
    const displayTotalStaked = displayMode === 'units' && advancedUnitSettings && unitStats ? unitStats.totalStaked : stats.totalStaked;
    const displayTotalReturned = displayMode === 'units' && advancedUnitSettings && unitStats ? unitStats.totalReturned : stats.totalReturned;

    const formatPercentage = (value) => {
        const prefix = value >= 0 ? '+' : '';
        return `${prefix}${value}%`;
    };

    const statCards = [
        {
            label: 'TOTAL P/L',
            value: formatValue(displayTotalPL),
            subLabel: 'ROI',
            subValue: formatPercentage(stats.roi),
            isPositive: displayTotalPL >= 0,
            highlight: true
        },
        {
            label: 'WIN RATE',
            value: `${stats.winRate}%`,
            subLabel: 'RECORD',
            subValue: `${stats.wins}W · ${stats.losses}L`,
            isNeutral: true
        },
        {
            label: 'TOTAL BETS',
            value: stats.totalBets.toLocaleString(),
            subLabel: stats.pending ? 'PENDING' : 'CASHED',
            subValue: stats.pending ? `${stats.pending}` : `${stats.cashedOut || 0}`,
            isNeutral: true
        },
        {
            label: 'AVG ODDS',
            value: stats.avgOdds.toFixed(2),
            subLabel: 'WIN AVG',
            subValue: stats.avgWinOdds,
            isNeutral: true
        },
        {
            label: 'STAKED',
            value: formatStaked(displayTotalStaked),
            subLabel: 'RETURNED',
            subValue: formatStaked(displayTotalReturned),
            isNeutral: true
        }
    ];

    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {statCards.map((stat, index) => {
                const valueColor = stat.isNeutral
                    ? 'text-white'
                    : stat.highlight
                        ? (displayTotalPL >= 0 ? 'text-emerald-400' : 'text-red-400')
                        : stat.isPositive
                            ? 'text-emerald-400'
                            : 'text-red-400';

                return (
                    <div
                        key={index}
                        className={`relative bg-[#0f172a] border border-slate-700/50 rounded overflow-hidden transition-all duration-500 ${
                            stat.highlight ? 'col-span-2 md:col-span-1' : ''
                        } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
                        style={{ transitionDelay: `${index * 40}ms` }}
                    >
                        {/* Highlight side accent */}
                        {stat.highlight && (
                            <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${displayTotalPL >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        )}

                        <div className={`${stat.highlight ? 'px-3.5 py-3 md:px-3.5 md:py-3' : 'px-3 py-2.5 md:px-3.5 md:py-3'}`}>
                            {/* Label row */}
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="text-[9px] md:text-[10px] font-mono font-semibold text-slate-500 tracking-[0.15em]">
                                    {stat.label}
                                </span>
                                {stat.highlight && (
                                    <div className="flex items-center gap-1">
                                        <div className={`w-1 h-1 rounded-full ${displayTotalPL >= 0 ? 'bg-emerald-500' : 'bg-red-500'} animate-pulse`} />
                                    </div>
                                )}
                            </div>

                            {/* Main value */}
                            <div className={`font-bold font-mono tabular-nums leading-tight tracking-tight truncate ${valueColor} ${
                                stat.highlight ? 'text-2xl md:text-xl' : 'text-lg md:text-xl'
                            }`}>
                                {stat.value}
                            </div>

                            {/* Sub value */}
                            <div className="mt-1.5 flex items-baseline justify-center gap-1.5 truncate">
                                <span className="text-[9px] font-mono text-slate-600 tracking-widest">
                                    {stat.subLabel}
                                </span>
                                <span className="text-[10px] font-mono text-slate-400 truncate">
                                    {stat.subValue}
                                </span>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default StatsOverview;
